/** The only photo-capture launcher — see ./CLAUDE.md */
import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { DataStoreService } from '@services/data-store-service';
import { EventLoggerService } from '@services/event-logger-service';
import { LocalStorageService } from '@services/local-storage-service';
import { AppConfig } from 'client/app/app.config';
import { buildExtraData, CaptureContext, CaptureIntent, CaptureMedium, CapturePage } from './capture-extra-data';

export interface PhotoCaptureRequest {
  tag: string;
  /**
   * The caller's whole context bag, forwarded as-is — never hand-picked. Persistence reads `tag`,
   * `fromReacquisition` and `fromTakePhotoBanner` out of it, so a bag built field by field silently
   * drops those behaviours. `tag` stays optional because a capture can legitimately have none.
   */
  context: CaptureContext;
  intent?: CaptureIntent;
  medium?: CaptureMedium;
  /** Reported on the gallery CleverTap event, which the same CTA now fires from two screens. */
  page?: CapturePage;
  persistContext?: boolean;
  logEvents?: boolean;
  webQueryParams?: Record<string, unknown>;
}

const CAPTURE_INITIATED_EVENT = 'instantCheckupInitiated';
const GALLERY_UPLOAD_EVENT = 'upload_from_gallery_clicked';

@Injectable({ providedIn: 'root' })
export class PhotoCaptureService {
  private readonly appConfig: AppConfig = inject(AppConfig);
  private readonly appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly dataStore: DataStoreService = inject(DataStoreService);
  private readonly document: Document = inject(DOCUMENT);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly router: Router = inject(Router);

  async launch(request: PhotoCaptureRequest): Promise<void> {
    const {
      tag, context, intent = CaptureIntent.NEW, medium = CaptureMedium.CAMERA,
      page, persistContext = true, logEvents = true, webQueryParams = {},
    }: PhotoCaptureRequest = request;
    const extraData: CaptureContext = buildExtraData(intent, context, medium);

    if (persistContext) this.persistCaptureContext(context);
    if (logEvents) this.trackLaunchEvents(tag, medium, page);

    if (!this.appWebBridge.isAppWebBridgeLoaded()) {
      if (medium === CaptureMedium.CAMERA) this.openWebFilePicker(tag, extraData, webQueryParams);
      return;
    }
    // Native performs the upload on both transports, so it needs a live session token either way.
    this.appWebBridge.notifyWebLoginToken();
    if (medium === CaptureMedium.GALLERY) {
      this.appWebBridge.requestGalleryImage(tag, {}, extraData);
      return;
    }
    await this.appWebBridge.notifyCaptureImage(tag, {}, extraData);
  }

  private persistCaptureContext(context: CaptureContext): void {
    this.localStorageService.setValue('instantCheckupExtraData', JSON.stringify(context));
    const contextTag: string | undefined = typeof context.tag === 'string' ? context.tag : undefined;
    if (contextTag && context.fromReacquisition !== 'true') {
      this.localStorageService.setValue('expectedFacePhoto', contextTag);
    }
    if (context.fromTakePhotoBanner) this.localStorageService.setValue('takePhotoBannerClicked', true);
  }

  private trackLaunchEvents(tag: string, medium: CaptureMedium, page?: CapturePage): void {
    if (medium === CaptureMedium.GALLERY) {
      this.eventLogger.trackEvent(GALLERY_UPLOAD_EVENT, { type: tag });
      this.eventLogger.cleverTapEvent(GALLERY_UPLOAD_EVENT, JSON.stringify({ page, type: tag }));
      return;
    }
    this.eventLogger.cleverTapEvent(CAPTURE_INITIATED_EVENT, JSON.stringify({ type: tag }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: CAPTURE_INITIATED_EVENT },
      firebase: { name: CAPTURE_INITIATED_EVENT },
    });
    this.eventLogger.trackEvent('take_photo_clicked', { type: tag });
    this.eventLogger.trackPeopleIncrement({ people_take_photo_clicked: 1 });
  }

  private openWebFilePicker(tag: string, extraData: CaptureContext, webQueryParams: Record<string, unknown>): void {
    const input: HTMLInputElement = this.document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.setAttribute('capture', 'user');
    input.className = 'tw-fixed -tw-top-full -tw-left-full';
    const removeInput = (): void => input.remove();
    input.addEventListener('change', (): void => {
      const file: File | undefined = input.files?.[0];
      removeInput();
      if (!file) return;
      this.dataStore.set('IMAGE_FILE', { file });
      this.router.navigate(['/user/instantCheckup/capture'], {
        queryParams: { ...extraData, ...webQueryParams, tag, nativeCameraFile: true },
        replaceUrl: true,
      });
    });
    input.addEventListener('cancel', removeInput);
    this.document.body.appendChild(input);
    input.click();
  }
}
